import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";
import { DEFAULT_THEME, isThemeId, type ThemeId } from "@/lib/themes";

const PUBLIC_THEME_KEY = "public_theme";

/** Public read: which palette signed-out visitors see by default. */
export const getPublicTheme = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const supabase = createClient<Database>(
      process.env["SUPABASE_URL"]!,
      process.env["SUPABASE_PUBLISHABLE_KEY"]!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data } = await supabase
      .from("site_settings")
      .select("value")
      .eq("key", PUBLIC_THEME_KEY)
      .maybeSingle();
    const value = data?.value;
    return { theme: (isThemeId(value) ? value : DEFAULT_THEME) as ThemeId };
  } catch {
    return { theme: DEFAULT_THEME as ThemeId };
  }
});

/** Admin only: set the palette signed-out visitors see. */
export const setPublicTheme = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { theme: string }) => {
    const theme = String(data?.theme ?? "");
    if (!isThemeId(theme)) throw new Error("Unknown theme");
    return { theme };
  })
  .handler(async ({ data, context }) => {
    const { data: isAdmin, error: roleError } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (roleError) throw new Error(roleError.message);
    if (!isAdmin) throw new Error("Admins only");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("site_settings")
      .upsert(
        { key: PUBLIC_THEME_KEY, value: data.theme, updated_at: new Date().toISOString() },
        { onConflict: "key" },
      );
    if (error) throw new Error(error.message);
    return { ok: true as const, theme: data.theme };
  });
